import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { History, Plus, Send, Trash2 } from 'lucide-react';
import { toast } from 'sonner';

import { SecretRevealDialog } from './SecretRevealDialog';
import { WebhookDeliveries, deliveriesQueryKey, type WebhookDelivery } from './WebhookDeliveries';

import { QueryError } from '@/components/QueryError';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { api } from '@/lib/api';
import { apiErrorMessage } from '@/lib/clients';
import { useHasPermission } from '@/lib/permissions';

// Réponse de GET /integrations/webhooks (tenants-service) : tableau simple
export interface Webhook {
  id: string; url: string; events: string[]; status: 'ACTIVE' | 'DISABLED';
  description: string | null; failCount: number; createdAt: string;
}

// Événements des flux lus par le worker de livraison (public-api, config webhooks.streams)
const EVENTS: { value: string; label: string }[] = [
  { value: 'mission.created', label: 'Mission créée' },
  { value: 'mission.started', label: 'Mission démarrée' },
  { value: 'mission.completed', label: 'Mission terminée' },
  { value: 'mission.cancelled', label: 'Mission annulée' },
  { value: 'alert.created', label: 'Alerte créée' },
  { value: 'alert.acked', label: 'Alerte prise en compte' },
  { value: 'alert.resolved', label: 'Alerte résolue' },
  { value: 'fuel.transaction_created', label: 'Plein saisi' },
  { value: 'fuel.theft_suspected', label: 'Vol de carburant suspecté' },
  { value: 'invoice.paid', label: 'Facture payée' },
  { value: 'payment.succeeded', label: 'Paiement réussi' },
  { value: 'payment.failed', label: 'Paiement échoué' },
];

const EMPTY_FORM = { url: '', events: [] as string[], description: '' };

/** Onglet « Webhooks » : liste sous integrations.read ; création, (dés)activation et suppression sous integrations.write. */
export function WebhooksTab() {
  const queryClient = useQueryClient();
  const canWrite = useHasPermission('integrations.write');
  const [creating, setCreating] = useState(false);
  const [form, setForm] = useState(EMPTY_FORM);
  const [revealed, setRevealed] = useState<string | null>(null);
  const [deleting, setDeleting] = useState<Webhook | null>(null);
  const [historyOf, setHistoryOf] = useState<string | null>(null);

  const webhooksQuery = useQuery({
    queryKey: ['webhooks'],
    queryFn: async () => (await api.get<Webhook[]>('/integrations/webhooks')).data,
  });
  const { data: webhooks } = webhooksQuery;
  const refresh = () => queryClient.invalidateQueries({ queryKey: ['webhooks'] });

  const create = useMutation({
    mutationFn: async () => {
      const body = { url: form.url.trim(), events: form.events, ...(form.description.trim() ? { description: form.description.trim() } : {}) };
      return (await api.post<Webhook & { secret: string }>('/integrations/webhooks', body)).data;
    },
    onSuccess: async (created) => {
      setCreating(false);
      setForm(EMPTY_FORM);
      setRevealed(created.secret);
      await refresh();
    },
  });

  const setStatus = useMutation({
    mutationFn: async ({ id, status }: { id: string; status: Webhook['status'] }) => {
      await api.patch(`/integrations/webhooks/${id}`, { status });
    },
    onSuccess: async (_d, { status }) => {
      toast.success(status === 'ACTIVE' ? 'Webhook activé' : 'Webhook désactivé');
      await refresh();
    },
    onError: (err) => toast.error(apiErrorMessage(err, 'Modification impossible')),
  });

  const remove = useMutation({
    mutationFn: async (id: string) => { await api.delete(`/integrations/webhooks/${id}`); },
    onSuccess: async () => {
      toast.success('Webhook supprimé');
      setDeleting(null);
      await refresh();
    },
    onError: (err) => toast.error(apiErrorMessage(err, 'Suppression impossible')),
  });

  // Ping signé envoyé tout de suite par l'API ; le résultat rejoint l'historique, ouvert pour le montrer
  const test = useMutation({
    mutationFn: async (id: string) => (await api.post<WebhookDelivery>(`/integrations/webhooks/${id}/test`)).data,
    onSuccess: async (delivery, id) => {
      if (delivery.status === 'SUCCEEDED') toast.success(`Test réussi (HTTP ${delivery.responseStatus})`);
      else toast.error(`Test échoué : ${delivery.errorMsg ?? 'pas de réponse'}`);
      setHistoryOf(id);
      await queryClient.invalidateQueries({ queryKey: deliveriesQueryKey(id) });
    },
    onError: (err) => toast.error(apiErrorMessage(err, 'Test impossible')),
  });

  const toggleEvent = (event: string) =>
    setForm((f) => ({ ...f, events: f.events.includes(event) ? f.events.filter((e) => e !== event) : [...f.events, event] }));

  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between space-y-0">
        <CardTitle>Webhooks</CardTitle>
        {canWrite && (
          <Button onClick={() => { create.reset(); setCreating(true); }}>
            <Plus className="h-4 w-4 mr-2" /> Ajouter un webhook
          </Button>
        )}
      </CardHeader>
      <CardContent>
        <div className="space-y-2">
          {webhooks?.map((w) => (
            <div key={w.id} className="p-3 border rounded">
              <div className="flex items-center justify-between gap-4">
                <div className="min-w-0">
                  <p className="font-mono text-sm text-muted-foreground truncate">{w.url}</p>
                  <p className="text-xs text-muted-foreground">{w.events.join(', ')}</p>
                  {w.failCount > 0 && <p className="text-xs text-danger">{w.failCount} échec(s) consécutif(s)</p>}
                </div>
                <div className="flex items-center gap-2 shrink-0">
                  <Badge variant={w.status === 'ACTIVE' ? 'success' : 'secondary'}>{w.status === 'ACTIVE' ? 'Actif' : 'Désactivé'}</Badge>
                  <Button size="sm" variant="ghost" aria-label={`Historique ${w.url}`} aria-expanded={historyOf === w.id}
                    onClick={() => setHistoryOf(historyOf === w.id ? null : w.id)}>
                    <History className="h-4 w-4" />
                  </Button>
                  {canWrite && (
                    <>
                      <Button size="sm" variant="outline" disabled={test.isPending} onClick={() => test.mutate(w.id)}>
                        <Send className="h-4 w-4 mr-1" /> {test.isPending && test.variables === w.id ? 'Envoi…' : 'Tester'}
                      </Button>
                      <Button size="sm" variant="outline" disabled={setStatus.isPending}
                        onClick={() => setStatus.mutate({ id: w.id, status: w.status === 'ACTIVE' ? 'DISABLED' : 'ACTIVE' })}>
                        {w.status === 'ACTIVE' ? 'Désactiver' : 'Activer'}
                      </Button>
                      <Button size="sm" variant="ghost" aria-label={`Supprimer ${w.url}`} onClick={() => setDeleting(w)}>
                        <Trash2 className="h-4 w-4 text-danger" />
                      </Button>
                    </>
                  )}
                </div>
              </div>
              {historyOf === w.id && <WebhookDeliveries endpointId={w.id} />}
            </div>
          ))}
          <QueryError query={webhooksQuery} />
          {webhooks?.length === 0 && <p className="text-muted-foreground text-sm">Aucun webhook configuré</p>}
        </div>
      </CardContent>

      <Dialog open={creating} onOpenChange={setCreating}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Nouveau webhook</DialogTitle>
            <DialogDescription>Requêtes POST signées (HMAC SHA-256, en-tête X-LogisTech-Signature) vers une URL https publique.</DialogDescription>
          </DialogHeader>
          <form id="webhook-form" className="space-y-3" onSubmit={(e) => { e.preventDefault(); create.mutate(); }}>
            <div className="space-y-1">
              <Label htmlFor="webhook-url">URL</Label>
              <Input id="webhook-url" type="url" required placeholder="https://" value={form.url}
                onChange={(e) => setForm({ ...form, url: e.target.value })} />
            </div>
            <fieldset className="space-y-1">
              <legend className="text-sm font-medium">Événements</legend>
              <div className="grid grid-cols-2 gap-1">
                {EVENTS.map((ev) => (
                  <label key={ev.value} className="flex items-center gap-2 text-sm">
                    <input type="checkbox" checked={form.events.includes(ev.value)} onChange={() => toggleEvent(ev.value)} />
                    {ev.label}
                  </label>
                ))}
              </div>
            </fieldset>
            <div className="space-y-1">
              <Label htmlFor="webhook-description">Description (facultatif)</Label>
              <Input id="webhook-description" maxLength={200} value={form.description}
                onChange={(e) => setForm({ ...form, description: e.target.value })} />
            </div>
            {create.isError && (
              <p role="alert" className="text-sm text-danger">{apiErrorMessage(create.error, 'Création impossible')}</p>
            )}
          </form>
          <DialogFooter>
            <Button variant="outline" type="button" onClick={() => setCreating(false)}>Annuler</Button>
            <Button type="submit" form="webhook-form" disabled={create.isPending || form.events.length === 0}>
              {create.isPending ? 'Création…' : 'Créer'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!deleting} onOpenChange={(open) => { if (!open) setDeleting(null); }}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Supprimer le webhook</DialogTitle>
            <DialogDescription>
              {deleting?.url} ne recevra plus aucun événement et son historique de livraisons sera effacé.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" type="button" onClick={() => setDeleting(null)}>Annuler</Button>
            <Button variant="destructive" disabled={remove.isPending} onClick={() => deleting && remove.mutate(deleting.id)}>
              {remove.isPending ? 'Suppression…' : 'Supprimer'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <SecretRevealDialog
        secret={revealed}
        title="Webhook créé"
        description="Secret de signature : il sert au système partenaire à vérifier l’en-tête X-LogisTech-Signature."
        onClose={() => setRevealed(null)}
      />
    </Card>
  );
}
